// Engine families and their sports (design section 2.1). SPORT_SCORING[*].family is
// unchanged; this is the engine family map. Pure data.
import type { ScoredSport } from "../../sport-scoring-versions";
import type { EngineFamily, EngineSport } from "./types";

export const ENGINE_FAMILIES = Object.freeze(["coastal", "sailing", "mtb"] as const satisfies readonly EngineFamily[]);

/** Sport to engine family. A typed record, so a new EngineSport fails to compile until it is mapped. */
export const ENGINE_FAMILY_BY_SPORT: Readonly<Record<EngineSport, EngineFamily>> = Object.freeze({
  surf: "coastal",
  bodyboarding: "coastal",
  wind: "coastal",
  foil: "coastal",
  "downwind-foil": "coastal",
  "wing-foil": "coastal",
  diving: "coastal",
  swim: "coastal",
  sup: "coastal",
  "sup-touring": "coastal",
  "sup-surf": "coastal",
  "sup-downwind": "coastal",
  "sailing-club": "sailing",
  "sailing-hp": "sailing",
  mtb: "mtb",
});

export const ENGINE_SPORTS: readonly EngineSport[] = Object.freeze(Object.keys(ENGINE_FAMILY_BY_SPORT) as EngineSport[]);

export const ENGINE_SPORTS_BY_FAMILY: Readonly<Record<EngineFamily, readonly EngineSport[]>> = Object.freeze({
  coastal: Object.freeze(ENGINE_SPORTS.filter((sport) => ENGINE_FAMILY_BY_SPORT[sport] === "coastal")),
  sailing: Object.freeze(ENGINE_SPORTS.filter((sport) => ENGINE_FAMILY_BY_SPORT[sport] === "sailing")),
  mtb: Object.freeze(ENGINE_SPORTS.filter((sport) => ENGINE_FAMILY_BY_SPORT[sport] === "mtb")),
});

/**
 * Ski sports that share daily_observations, condition_scores and sport_hourly_scores rows
 * with MTB. The whole-row guard therefore couples MTB with ski (design 8.4): the MTB fence
 * and revision mode are evaluated with these sports.
 */
export const MTB_ROW_PARTNERS = Object.freeze(["piste", "off-piste", "touring"] as const satisfies readonly ScoredSport[]);

export function isEngineSport(value: string): value is EngineSport {
  return Object.hasOwn(ENGINE_FAMILY_BY_SPORT, value);
}

export function isEngineFamily(value: string): value is EngineFamily {
  return (ENGINE_FAMILIES as readonly string[]).includes(value);
}

export function engineFamilyOf(sport: EngineSport): EngineFamily {
  return ENGINE_FAMILY_BY_SPORT[sport];
}

/** The v2 safety flag's name (core/flags.ts ENGINE_FLAG_NAMES.safetyPolicyV2Sports; a test holds them equal). */
export const SAFETY_POLICY_V2_SPORTS_FLAG = "CONDITIONS_SAFETY_POLICY_V2_SPORTS";

/**
 * CONDITIONS_SAFETY_POLICY_V2_SPORTS for page components, which may import only the declaration
 * modules (flags.ts is not one: core/__tests__/engine-importers.test.ts). The same grammar and
 * fail-closed rule as flags.ts parseSportList: exact ids, whitespace around entries ignored, empty
 * entries skipped, and an entry naming no non-ski sport empties the list. The sports a flag lists,
 * which is the request: whether v2 is effective also needs the sport's safety module and family
 * engine (core/safety-carrier.ts), which pages cannot import.
 */
export function safetyPolicyV2SportsFromEnv(env: Readonly<Record<string, string | undefined>>): readonly EngineSport[] {
  const entries = (env[SAFETY_POLICY_V2_SPORTS_FLAG] ?? "").split(",").map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  if (!entries.every(isEngineSport)) return Object.freeze([]);
  const listed = new Set<string>(entries);
  return Object.freeze(ENGINE_SPORTS.filter((sport) => listed.has(sport)));
}
